import type { Landmark } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { ringPoints } from '../solids';
import { vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './nieuweNonnenPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function nieuweNonnenFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Nieuwe Nonnen lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const nieuweNonnenChapel = () => ({ ...reading.chapel, apse: 'flat' as const });

export function nieuweNonnenRangeFrame(
  plan: PlanFrame,
  part: (typeof reading.ranges)[number]
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export function nieuweNonnenFoundations(landmark: Landmark): Ring[] {
  const plan = nieuweNonnenFrame(landmark);
  return [
    vesselOutline(nieuweNonnenChapel()).map((p) => toLayout(plan, p)),
    ...reading.ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = nieuweNonnenRangeFrame(plan, part),
        rooms = part.passage
          ? [
              { ...rect, u1: (part.passageU ?? 0) - part.passage / 2 },
              { ...rect, u0: (part.passageU ?? 0) + part.passage / 2 },
            ]
          : [rect];
      return rooms.map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function nieuweNonnenObstacles(landmark: Landmark): Ring[] {
  const plan = nieuweNonnenFrame(landmark);
  return [
    ...nieuweNonnenFoundations(landmark),
    ...conventEnclosureRings(reading.walls).map((ring) => ring.map((p) => toLayout(plan, p))),
    ...reading.trees.map((p) =>
      ringPoints([p[0]!, p[1]!], 0.15, 5, 0).map((q) => toLayout(plan, q))
    ),
  ];
}

/** The open bleaching court stays separate from the small herb plot and orchard. */
export function nieuweNonnenFloors(landmark: Landmark) {
  const plan = nieuweNonnenFrame(landmark);
  return [
    { ring: reading.courts.map((p): Vec2 => [p[0]!, p[1]!]), height: 0.025, garden: false },
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => ({
        ring: corners({
          u0: (p.passageU ?? 0) - p.passage / 2,
          u1: (p.passageU ?? 0) + p.passage / 2,
          v0: -p.width / 2,
          v1: p.width / 2,
        }).map((q) =>
          toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
        ),
        height: 0.025,
        garden: false,
      })),
    { ring: corners(reading.orchard), height: 0.03, garden: true },
    { ring: corners(reading.herbs), height: 0.05, garden: true },
  ].map((f) => ({ ...f, ring: f.ring.map((p: Vec2) => toLayout(plan, p)) }));
}
